import React, { useEffect, useState } from "react";
import API from "../services/api";

// Helper to get local date as YYYY-MM-DD
function getLocalYYYYMMDD() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().split("T")[0];
}

function TherapistMultiClientDailyLogPage() {
  const today = getLocalYYYYMMDD();

  // Prepare up to 10 new daily log entries
  const initialEntries = Array.from({ length: 10 }, () => ({
    clientName: "",
    paymentType: "Card",
    collectedAmount: "0",
    serviceType: "90834 - Individual Psychotherapy",
    customService: "",
    billedAmount: "",
    notes: "",
    sessionDate: today,
    noShow: false,
    cancelation: false,
  }));

  const [entries, setEntries] = useState(initialEntries);
  const [clients, setClients] = useState([]);

  // For adding a new client
  const [newClientFirstName, setNewClientFirstName] = useState("");
  const [newClientLastName, setNewClientLastName] = useState("");

  // For displaying and editing UNPAID logs
  const [unpaidLogs, setUnpaidLogs] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    serviceType: "",
    billedAmount: "",
    collectedAmount: "",
    paymentType: "",
    notes: "",
    sessionDate: "",
  });

  // =========== Fetch Existing Clients ==========
  const fetchClients = async () => {
    try {
      const res = await API.get("/therapist/clients");
      // Sort them by name
      const sorted = res.data.sort((a, b) =>
        a.client_name.localeCompare(b.client_name)
      );
      setClients(sorted);
    } catch (error) {
      console.error(error);
      alert("Error fetching clients");
    }
  };

  // =========== Fetch UNPAID Logs ==========
  const fetchUnpaidLogs = async () => {
    try {
      const res = await API.get("/therapist/daily-logs/unpaid");
      setUnpaidLogs(res.data);
    } catch (err) {
      console.error(err);
      alert("Error fetching unpaid logs");
    }
  };

  // =========== Add New Client ==========
  const handleAddNewClient = async () => {
    const trimmedFirst = newClientFirstName.trim();
    const trimmedLast = newClientLastName.trim();
    if (!trimmedFirst || !trimmedLast) {
      alert("Please enter both first name and last name.");
      return;
    }

    // Capitalize function
    const cap = (s) =>
      s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

    const finalFirst = cap(trimmedFirst);
    const finalLast = cap(trimmedLast);
    const combinedName = `${finalLast}, ${finalFirst}`;

    // Check if client already exists
    const alreadyExists = clients.find(
      (c) => c.client_name.toLowerCase() === combinedName.toLowerCase()
    );
    if (alreadyExists) {
      alert("A client with this name already exists.");
      return;
    }
    try {
      await API.post("/therapist/clients", {
        clientName: combinedName,
      });
      alert(`Created new client: ${combinedName}`);
      setNewClientFirstName("");
      setNewClientLastName("");
      fetchClients();
    } catch (err) {
      console.error(err);
      alert("Error adding new client");
    }
  };

  // =========== Handle Changes in the Bulk Table ==========
  const handleChange = (index, field, value) => {
    setEntries((prev) => {
      const newEntries = [...prev];
      newEntries[index] = { ...newEntries[index], [field]: value };
      return newEntries;
    });
  };

  // =========== Submit the Bulk Daily Log ==========
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validEntries = entries
      .filter((entry) => entry.clientName && entry.sessionDate)
      .map((entry) => {
        let finalServiceType = entry.serviceType;
        if (finalServiceType === "Other" && entry.customService.trim()) {
          finalServiceType = entry.customService.trim();
        }
        return {
          clientName: entry.clientName,
          serviceType: finalServiceType,
          billedAmount: entry.billedAmount || 0,
          collectedAmount: entry.collectedAmount || 0,
          paymentType: entry.paymentType.toUpperCase(),
          notes: entry.notes || "",
          sessionDate: entry.sessionDate,
          noShow: entry.noShow,
          cancelation: entry.cancelation,
        };
      });
    if (validEntries.length === 0) {
      alert("No valid entries to submit! Please fill at least one row.");
      return;
    }
    try {
      const res = await API.post("/therapist/daily-log/bulk", {
        entries: validEntries,
      });
      alert(`Success! Inserted ${res.data.count} sessions.`);
      setEntries(initialEntries);
      fetchUnpaidLogs(); // refresh the UNPAID logs
    } catch (error) {
      console.error(error);
      alert("Error submitting multi client daily log");
    }
  };

  // =========== Delete UNPAID Entry ==========
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await API.delete(`/therapist/daily-logs/${id}`);
      fetchUnpaidLogs();
    } catch (err) {
      console.error(err);
      alert("Error deleting entry");
    }
  };

  // =========== Edit UNPAID Entry ==========
  const startEdit = (log) => {
    setEditId(log.id);
    setEditForm({
      serviceType: log.service_type,
      billedAmount: log.billed_amount,
      collectedAmount: log.collected_amount,
      paymentType: log.payment_type,
      notes: log.notes || "",
      sessionDate: log.session_date.slice(0, 10),
    });
  };

  // =========== Save Edited UNPAID Entry ==========
  const saveEdit = async () => {
    try {
      await API.put(`/therapist/daily-logs/${editId}`, {
        serviceType: editForm.serviceType,
        billedAmount: editForm.billedAmount,
        collectedAmount: editForm.collectedAmount,
        paymentType: editForm.paymentType,
        notes: editForm.notes,
        sessionDate: editForm.sessionDate,
      });
      setEditId(null);
      fetchUnpaidLogs();
    } catch (err) {
      console.error(err);
      alert("Error saving edits");
    }
  };

  // =========== Cancel Edit ==========
  const cancelEdit = () => {
    setEditId(null);
  };

  useEffect(() => {
    fetchClients();
    fetchUnpaidLogs();
  }, []);

  return (
    <div style={{ margin: "1rem", background: "#f8f8f8", padding: "1rem" }}>
      <style>{`
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
        
        /* Make the table more responsive for mobile */
        @media (max-width: 750px) {
          /* Hide the table header (we'll use data-label in each cell) */
          .multi-client-table table thead {
            display: none;
          }

          .multi-client-table table tbody tr, 
          .multi-client-table table tbody td {
            display: block;
            width: 100%;
          }

          .multi-client-table table tbody td {
            margin-bottom: 0.75rem;
          }

          .multi-client-table table tbody td:before {
            content: attr(data-label);
            font-weight: bold;
            display: inline-block;
            width: 40%;
            margin-right: 0.5rem;
          }
        }

        /* For the UNPAID logs, convert to cards in mobile */
        @media (max-width: 750px) {
          .unpaid-table {
            display: none; /* hide the table in mobile */
          }
          .unpaid-cards {
            display: block; 
          }
          .unpaid-card {
            background: #ffffff;
            margin-bottom: 1rem;
            padding: 1rem;
            border: 1px solid #ccc;
            border-radius: 6px;
          }
          .unpaid-card .item {
            margin-bottom: 0.5rem;
          }
          .unpaid-card .item span {
            font-weight: bold;
            margin-right: 0.25rem;
          }
        }

        @media (min-width: 751px) {
          .unpaid-cards {
            display: none; /* hide card format on desktop */
          }
        }
      `}</style>

      <h2>Multi-Client Daily Log (Up to 10)</h2>

      {/* Instructions at the top */}
      <div style={{ marginBottom: "1rem", background: "#ffffff", padding: "0.5rem" }}>
        <strong>How to Use:</strong> Enter each client’s details below.
        Mark <em>No Show</em> if the client did not attend (and you’re charging),
        or <em>Cancelation</em> if they canceled in advance (and you’re not charging).
        Then click <em>Submit</em>.
      </div>

      {/* 1) Add New Client Section */}
      <div
        style={{
          border: "1px solid #ccc",
          padding: "1rem",
          marginBottom: "1rem",
          background: "#fff",
        }}
      >
        <h3>Add New Client</h3>
        <div style={{ marginBottom: "0.5rem" }}>
          <label style={{ display: "inline-block", width: "90px" }}>
            First Name:
          </label>
          <input
            type="text"
            value={newClientFirstName}
            onChange={(e) => setNewClientFirstName(e.target.value)}
            style={{ marginRight: "1rem" }}
          />
        </div>
        <div style={{ marginBottom: "0.5rem" }}>
          <label style={{ display: "inline-block", width: "90px" }}>
            Last Name:
          </label>
          <input
            type="text"
            value={newClientLastName}
            onChange={(e) => setNewClientLastName(e.target.value)}
            style={{ marginRight: "1rem" }}
          />
        </div>
        <button onClick={handleAddNewClient}>Add Client</button>
      </div>

      {/* 2) Bulk Daily Log Table */}
      <form onSubmit={handleSubmit}>
        <div className="multi-client-table" style={{ overflowX: "auto" }}>
          <table border="1" cellPadding="5" style={{ background: "#fff", width: "100%" }}>
            <thead>
              <tr>
                <th>Client*</th>
                <th>Service</th>
                <th>Billed</th>
                <th>Collected</th>
                <th>Payment</th>
                <th>Notes</th>
                <th>Date*</th>
                <th>No Show?</th>
                <th>Cancelation?</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, index) => (
                <tr key={index}>
                  <td data-label="Client*">
                    <select
                      value={entry.clientName}
                      onChange={(e) =>
                        handleChange(index, "clientName", e.target.value)
                      }
                      style={{ width: "100%" }}
                    >
                      <option value="">-- Select Client --</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.client_name}>
                          {client.client_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td data-label="Service">
                    <select
                      value={entry.serviceType}
                      onChange={(e) =>
                        handleChange(index, "serviceType", e.target.value)
                      }
                      style={{ width: "100%" }}
                    >
                      <option value="90837 - Individual Psychotherapy 60 min">
                        90837 - Individual Psychotherapy 60 min
                      </option>
                      <option value="90847 - Family Psychotherapy 50 min">
                        90847 - Family Psychotherapy
                      </option>
                      <option value="90853 - Group Psychotherapy 50">
                        90853 - Group Psychotherapy
                      </option>
                      <option value="90832 - Individual Psychotherapy 30 min">
                        90832 - Individual Psychotherapy 30 min
                      </option>
                     
                      <option value="99354 - Add-on/Additional 30 min">
                        99354 - Add-on/Additional 30 min
                      </option>
                      <option value="Other">Other (enter custom)</option>
                    </select>
                    {entry.serviceType === "Other" && (
                      <div style={{ marginTop: "0.5rem" }}>
                        <input
                          type="text"
                          placeholder="Enter custom service/CPT"
                          value={entry.customService}
                          onChange={(e) =>
                            handleChange(index, "customService", e.target.value)
                          }
                          style={{ width: "100%" }}
                        />
                      </div>
                    )}
                  </td>
                  <td data-label="Billed">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={entry.billedAmount}
                      onChange={(e) =>
                        handleChange(index, "billedAmount", e.target.value)
                      }
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td data-label="Collected">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={entry.collectedAmount}
                      onChange={(e) =>
                        handleChange(index, "collectedAmount", e.target.value)
                      }
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td data-label="Payment">
                    <select
                      value={entry.paymentType}
                      onChange={(e) =>
                        handleChange(index, "paymentType", e.target.value)
                      }
                      style={{ width: "100%" }}
                    >
                      <option value="Card">Card</option>
                      <option value="Cash">Cash</option>
                      <option value="Check">Check</option>
                    </select>
                  </td>
                  <td data-label="Notes">
                    <input
                      type="text"
                      value={entry.notes}
                      onChange={(e) =>
                        handleChange(index, "notes", e.target.value)
                      }
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td data-label="Date*">
                    <input
                      type="date"
                      value={entry.sessionDate}
                      onChange={(e) =>
                        handleChange(index, "sessionDate", e.target.value)
                      }
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td data-label="No Show?">
                    <input
                      type="checkbox"
                      checked={entry.noShow}
                      onChange={(e) =>
                        handleChange(index, "noShow", e.target.checked)
                      }
                    />
                  </td>
                  <td data-label="Cancelation?">
                    <input
                      type="checkbox"
                      checked={entry.cancelation}
                      onChange={(e) =>
                        handleChange(index, "cancelation", e.target.checked)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="submit" style={{ marginTop: "10px", padding: "5px 15px" }}>
          Submit
        </button>
      </form>

      {/* 3) UNPAID Entries Table */}
      <h3 style={{ marginTop: "2rem" }}>Submitted and Waiting on Admin Approval:</h3>

      {/* Desktop Table */}
      <div className="unpaid-table" style={{ overflowX: "auto" }}>
        <table border="1" cellPadding="5" style={{ width: "100%", background: "#fff" }}>
          <thead>
            <tr>
              <th>Client</th>
              <th>Service</th>
              <th>Billed</th>
              <th>Collected</th>
              <th>Payment</th>
              <th>Notes</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {unpaidLogs.map((log) => {
              const isEditing = editId === log.id;
              if (isEditing) {
                // Editing row
                return (
                  <tr key={log.id}>
                    <td>{log.client_name}</td>
                    <td>
                      <input
                        type="text"
                        value={editForm.serviceType}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            serviceType: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={editForm.billedAmount}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            billedAmount: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={editForm.collectedAmount}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            collectedAmount: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td>
                      <select
                        value={editForm.paymentType}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            paymentType: e.target.value,
                          })
                        }
                      >
                        <option value="CARD">Card</option>
                        <option value="CASH">Cash</option>
                        <option value="CHECK">Check</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editForm.notes}
                        onChange={(e) =>
                          setEditForm({ ...editForm, notes: e.target.value })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        value={editForm.sessionDate}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            sessionDate: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td>
                      <button onClick={saveEdit}>Save</button>
                      <button onClick={cancelEdit}>Cancel</button>
                    </td>
                  </tr>
                );
              } else {
                // Normal display row
                return (
                  <tr key={log.id}>
                    <td>{log.client_name}</td>
                    <td>{log.service_type}</td>
                    <td>{log.billed_amount}</td>
                    <td>{log.collected_amount}</td>
                    <td>{log.payment_type}</td>
                    <td>{log.notes}</td>
                    <td>{log.session_date.slice(0, 10)}</td>
                    <td>
                      <button onClick={() => startEdit(log)}>Edit</button>
                      <button onClick={() => handleDelete(log.id)}>Delete</button>
                    </td>
                  </tr>
                );
              }
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card Layout */}
      <div className="unpaid-cards">
        {unpaidLogs.map((log) => {
          const isEditing = editId === log.id;
          if (isEditing) {
            return (
              <div className="unpaid-card" key={log.id}>
                <div className="item">
                  <span>Client:</span> {log.client_name}
                </div>
                <div className="item">
                  <span>Service:</span>
                  <input
                    type="text"
                    value={editForm.serviceType}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        serviceType: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="item">
                  <span>Billed:</span>
                  <input
                    type="number"
                    value={editForm.billedAmount}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        billedAmount: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="item">
                  <span>Collected:</span>
                  <input
                    type="number"
                    value={editForm.collectedAmount}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        collectedAmount: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="item">
                  <span>Payment:</span>
                  <select
                    value={editForm.paymentType}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        paymentType: e.target.value,
                      })
                    }
                  >
                    <option value="CARD">Card</option>
                    <option value="CASH">Cash</option>
                    <option value="CHECK">Check</option>
                  </select>
                </div>
                <div className="item">
                  <span>Notes:</span>
                  <input
                    type="text"
                    value={editForm.notes}
                    onChange={(e) =>
                      setEditForm({ ...editForm, notes: e.target.value })
                    }
                  />
                </div>
                <div className="item">
                  <span>Date:</span>
                  <input
                    type="date"
                    value={editForm.sessionDate}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        sessionDate: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="item">
                  <button onClick={saveEdit}>Save</button>
                  <button onClick={cancelEdit}>Cancel</button>
                </div>
              </div>
            );
          } else {
            return (
              <div className="unpaid-card" key={log.id}>
                <div className="item">
                  <span>Client:</span> {log.client_name}
                </div>
                <div className="item">
                  <span>Service:</span> {log.service_type}
                </div>
                <div className="item">
                  <span>Billed:</span> {log.billed_amount}
                </div>
                <div className="item">
                  <span>Collected:</span> {log.collected_amount}
                </div>
                <div className="item">
                  <span>Payment:</span> {log.payment_type}
                </div>
                <div className="item">
                  <span>Notes:</span> {log.notes}
                </div>
                <div className="item">
                  <span>Date:</span> {log.session_date.slice(0, 10)}
                </div>
                <div className="item">
                  <button onClick={() => startEdit(log)}>Edit</button>
                  <button onClick={() => handleDelete(log.id)}>Delete</button>
                </div>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
}

export default TherapistMultiClientDailyLogPage;
